// src/pages/ProductsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import EditIcon from '@mui/icons-material/Edit';
import CategoryIcon from '@mui/icons-material/Category';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { DataGrid } from '@mui/x-data-grid';

import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';


const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- Petit composant "carte stat"
const SummaryCard = ({ title, value, icon, bgColor, borderColor, onClick, subtitle }) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      backgroundColor: bgColor || 'background.paper',
      border: borderColor || '1px solid rgba(0,0,0,0.08)',
      transition: 'transform .08s ease',
      '&:hover': onClick ? { transform: 'translateY(-2px)' } : undefined,
    }}
  >
    <CardContent sx={{ display: 'grid', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h5">{value}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export default function Products() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);           // 0-based pour MRT
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Dialogs (exemples: liste supprimés)
  const [openDeletedDialog, setOpenDeletedDialog] = useState(false);

  // --- Fetch coté serveur (paginé)
  const fetchProducts = async (p = page, l = pageSize, q = search) => {
    setLoading(true);
    try {
      const res = await api.get('/api/products', {
        params: {
          page: p + 1,           // API en 1-based
          limit: l,
          search: q || undefined,
          includeDeleted: includeDeleted,
        },
      });
      let data = res.data?.data || [];

      // Si tu veux filtrer par nom d’organisation (contexte), fais-le ici si ton API ne le gère pas:

      setRows(data);
      setTotal(res.data?.pagination?.total || data.length);
    } catch (e) {
      console.error('Fetch products failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(0, pageSize, '');
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted]);

  // --- Stats
  const totalCount = total;
  const deletedCount = rows.filter((r) => !!r.deleted).length;
  const activeCount = rows.filter((r) => !r.deleted).length;

  // --- Dialog rows (supprimés)
  const deletedRows = rows
    .filter((r) => r.deleted)
    .map((r, i) => ({
      id: r._id || i,
      name: r.name,
      category: r.category || '',
      status: r.status || '',
      deletedAt: r.deleted ? new Date(r.deleted).toLocaleString('fr-FR') : '',
      raw: r,
    }));

  const categoryOptions = useMemo(() => {
    const set = new Set(
      rows.map((r) => (r.category || '').trim()).filter(Boolean)
    );
    return Array.from(set).sort();
  }, [rows]);

  const statusOptions = useMemo(() => {
    const set = new Set(
      rows.map((r) => (r.status || '').trim()).filter(Boolean)
    );
    if (set.size === 0) ['draft', 'active', 'archived'].forEach((v) => set.add(v));
    return Array.from(set).sort();
  }, [rows]);

  // --- Colonnes MRT (style Domaines)
  const columns = useMemo(
    () => [
      {
        id: 'products',
        columns: [
          {
            accessorKey: 'name',
            header: 'Nom',
            size: 250,
            enableClickToCopy: true,
            filterVariant: 'autocomplete',
            Cell: ({ cell }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory2Icon fontSize="small" />
                <Typography variant="body2">{cell.getValue()}</Typography>
              </Box>
            ),
          },
           {
            accessorKey: 'code',
            header: 'Code produit',
            size: 100,
            enableClickToCopy: true,
            filterVariant: 'autocomplete',
            Cell: ({ cell }) => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">{cell.getValue()}</Typography>
              </Box>
            ),
          },
          {
            accessorKey: 'category',
            header: 'Catégorie',
            size: 180,
            filterVariant: 'autocomplete',
            // Affichage
            Cell: ({ cell }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CategoryIcon fontSize="small" />
                <Typography variant="body2">{cell.getValue() || '—'}</Typography>
              </Box>
            ),
            // Édition: select + saisie libre
            renderEditCell: ({ cell }) => (
              <Autocomplete
                freeSolo
                options={categoryOptions}
                value={cell.getValue() ?? ''}
                onChange={(_, val) => cell.setValue(val ?? '')}
                onInputChange={(_, val) => cell.setValue(val ?? '')}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Catégorie" placeholder="ex: Laptop" />
                )}
              />
            ),
          },
          {
            accessorKey: 'description',
            header: 'Description',
            size: 300,
            filterVariant: 'autocomplete',
            Cell: ({ cell }) => (
              <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>
                {cell.getValue() || '—'}
              </Typography>
            ),
          },
          {
            accessorKey: 'status',
            header: 'Statut',
            size: 120,
            filterVariant: 'select',
          // ✅ rendu en select dans le formulaire d’édition/création
            // editVariant: 'select',
            Cell: ({ cell }) => {
              const v = cell.getValue();
              const map = {
                active: { label: 'Actif', color: 'success' },
                draft: { label: 'Brouillon', color: 'default' },
                archived: { label: 'Archivé', color: 'warning' },
              };
              const cfg = map[v] || { label: v || '—', color: 'default' };
              return <Chip size="small" label={cfg.label} color={cfg.color} />;
            },
          },
          {
            id: 'deleted',
            header: 'Supprimé',
            size: 100,
            accessorFn: (row) => (!!row.deleted ? 'Actif' : 'Supprimé'),
            Cell: ({ row }) =>
              row.original.deleted ? (
                <Chip size="small" label="Supprimé" color="error" />
              ) : (
                <Chip size="small" label="Actif" />
              ),
            filterVariant: 'select',
          },
        ],
      },
    ],
    []
  );

  // --- Table MRT
  const table = useMaterialReactTable({
    columns,
    data: rows,
    state: { isLoading: loading, showProgressBars: loading },
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: {
      density: 'compact',
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
      pagination: { pageSize },
    },
    onPaginationChange: ({ pageIndex, pageSize: ps }) => {
      setPage(pageIndex);
      setPageSize(ps);
      fetchProducts(pageIndex, ps, search);
    },
    manualPagination: true,
    rowCount: totalCount,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined', placeholder: 'Rechercher…' },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [25, 50, 100, 200],
      shape: 'rounded',
      variant: 'outlined',
    },
    onGlobalFilterChange: (val) => {
      setSearch(val || '');
      fetchProducts(0, pageSize, val || '');
      setPage(0);
    },
    enableEditing: true,
    getRowId: (row) => row._id,
    onCreatingRowSave: async ({ values, table }) => {
    // petite validation front
    if (!values.name || !values.name.trim()) {
      table.setCreatingRowError('Le nom est requis');
      return;
    }
    try {
      await api.post('/api/products', {
        name: values.name.trim(),
        category: values.category || '',
        description: values.description || '',
        status: values.status || 'draft',
        // Organization: values.Organization || null, // si tu ajoutes ce champ
      });
      await fetchProducts(page, pageSize, search);
      table.setCreatingRow(null); // ferme la ligne
    } catch (e) {
      console.error('Create failed:', e);
      table.setCreatingRowError("Échec de la création");
    }
  },
  onCreatingRowCancel: () => {},

  // ✏️ EDITION (tu gardes le tien)
  onEditingRowSave: async ({ values, row, table }) => {
    try {
      await api.put(`/api/products/${row.original._id}`, {
        name: values.name?.trim(),
        category: values.category || '',
        description: values.description || '',
        status: values.status || 'draft',
      });
      await fetchProducts(page, pageSize, search);
      table.setEditingRow(null);
    } catch (e) {
      console.error('Update failed:', e);
      table.setEditingRowError("Échec de la mise à jour");
    }
  },
    // onEditingRowSave: async ({ values, row, table }) => {
    //   try {
    //     if (row.original?._id) {
    //       await api.put(`/api/products/${row.original._id}`, {
    //         name: values.name?.trim(),
    //         category: values.category || '',
    //         description: values.description || '',
    //         status: values.status || 'draft',
    //       });
    //     } else {
    //       await api.post(`/api/products`, {
    //         name: values.name?.trim(),
    //         category: values.category || '',
    //         description: values.description || '',
    //         status: values.status || 'draft',
    //       });
    //     }
    //     await fetchProducts(page, pageSize, search);
    //     table.setEditingRow(null);
    //   } catch (e) {
    //     console.error('Save failed:', e);
    //   }
    // },
    onEditingRowCancel: () => {},
renderRowActions: ({ row, table }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Tooltip title="Éditer">
      <IconButton
        size="small"
        onClick={() => table.setEditingRow(row)}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>

    {!row.original.deleted ? (
      <Tooltip title="Supprimer">
        <IconButton
          size="small"
          onClick={async () => {
            try {
              await api.delete(`/api/products/${row.original._id}`);
              await fetchProducts(page, pageSize, search);
            } catch (e) {
              console.error('Delete failed:', e);
            }
          }}
        >
          <DeleteForeverIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : (
      <Tooltip title="Restaurer">
        <IconButton
          size="small"
          color="primary"
          onClick={async () => {
            try {
              await api.post(`/api/products/${row.original._id}/restore`);
              await fetchProducts(page, pageSize, search);
            } catch (e) {
              console.error('Restore failed:', e);
            }
          }}
        >
          <RestoreFromTrashIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Box>
),
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DeleteForeverIcon />}
          color={includeDeleted ? 'error' : 'inherit'}
          onClick={() => setIncludeDeleted((v) => !v)}
        >
          {includeDeleted ? 'Masquer supprimés' : 'Afficher supprimés'}
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<AssignmentTurnedInIcon />}
          onClick={() => {
            // Ajout inline
            table.setCreatingRow(true);
          }}
        >
          Nouveau produit
        </Button>
      </Box>
    ),
    editDisplayMode: 'row', // édition inline (ligne)
    createDisplayMode: 'row',
  });

  return (
    <Box>
      <Typography variant="h6" my={1}>Produits</Typography>

      {/* Bandeau stats (même esprit que Domaines) */}
      <Grid container spacing={2} my={2}>
        <Grid item size={{ xs: 12, md: 3 }}>
          <SummaryCard
            title="Total"
            value={totalCount}
            icon={
              <Avatar sx={{ bgcolor: 'black' }}>
                <Inventory2Icon />
              </Avatar>
            }
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3 }}>
          <SummaryCard
            title="Actifs"
            value={activeCount}
            icon={
              <Avatar sx={{ bgcolor: 'success.main' }}>
                <AssignmentTurnedInIcon />
              </Avatar>
            }
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3 }}>
          <SummaryCard
            title="Supprimés"
            value={deletedCount}
            icon={
              <Avatar sx={{ bgcolor: 'error.main' }}>
                <DeleteForeverIcon />
              </Avatar>
            }
            bgColor={deletedCount > 0 ? '#ffebee' : undefined}
            borderColor={deletedCount > 0 ? '1px solid #d32f2f' : undefined}
            onClick={() => setOpenDeletedDialog(true)}
          />
        </Grid>
      </Grid>

      {/* Table principale (MRT) */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Liste des produits
        </Typography>
        <MaterialReactTable table={table} />
      </Box>

      {/* Dialog: produits supprimés */}
      <Dialog open={openDeletedDialog} onClose={() => setOpenDeletedDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Produits supprimés</DialogTitle>
        <DialogContent>
          <DataGrid
            autoHeight
            rows={deletedRows}
            columns={[
              { field: 'name', headerName: 'Nom', flex: 1 },
              { field: 'category', headerName: 'Catégorie', flex: 1 },
              { field: 'status', headerName: 'Statut', flex: 1 },
              { field: 'deletedAt', headerName: 'Supprimé le', flex: 1 },
            ]}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            density="compact"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeletedDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
